/**
 * Quizkosh — Exam Generation Service (Phase 1)
 *
 * Single entry-point that future exam-aware pipelines will use. In Phase 1
 * it is a *pass-through*: every exam (including NEET) is routed to the
 * existing legacy `generateQuiz()` pipeline. This guarantees zero behavior
 * change while giving Phase 2 a stable seam to plug in NEET-specific logic.
 *
 * NO AI CALLS HAPPEN HERE BEYOND WHAT THE LEGACY PIPELINE ALREADY DOES.
 */

import { getCapabilities, isAdvancedExam, examCodeForLabel } from "./registry";
import { normalizeSource } from "./sourceNormalization";

/**
 * Resolve the exam code for a given quiz config. Lookups in order:
 *  1. Explicit `cfg.examCode`
 *  2. Mapped from `cfg.category` label
 *  3. null  (= legacy path)
 */
export function resolveExamCode(cfg) {
  if (cfg?.examCode) return cfg.examCode;
  if (cfg?.category) return examCodeForLabel(cfg.category);
  return null;
}

/**
 * Phase-1 router. Returns metadata about *which* pipeline would run, so the
 * UI/caller can decide whether to surface an "Exam Intelligence" badge or
 * just keep the standard flow. Does NOT run generation.
 *
 * Phase 2: this is where NEET (and other advanced exams) will dispatch to
 * `runNeetPipeline(envelope)` etc.
 */
export function planExamGeneration(cfg) {
  const examCode = resolveExamCode(cfg);
  const caps = getCapabilities(examCode);
  const envelope = normalizeSource({ ...cfg, examCode });
  return {
    examCode,
    capabilities: caps,
    envelope,
    pipeline: isAdvancedExam(examCode) ? "advanced_passthrough" : "legacy",
    // Phase-1 contract: never short-circuit the legacy pipeline.
    useLegacyGeneration: true,
  };
}

/**
 * Convenience wrapper for callers that just need to know whether to show
 * the new "Exam Intelligence Mode" tile on the source-picker.
 */
export function supportsExamIntelligenceMode(cfg) {
  const examCode = resolveExamCode(cfg);
  return getCapabilities(examCode).examIntelligenceMode === true;
}
